import { useQuery } from '@apollo/client/react';
import { type FunctionComponent } from 'react';
import { type RecommendationsQuery, type RecommendationsQueryVariables } from '../../../__generated__/graphql-client-types';
import { defaultCloudinaryOptions } from '../../../constants/images';
import { buildGTMFindingMethodDynamicRecommendation } from '../../../helpers/analytics/gtm/search-event-builders';
import { useRouterLocation } from '../../../hooks/routing/location.hook';
import { RECOMMENDATIONS, REPORT_RECOMMENDATION_ENGAGEMENT } from '../../../queries/dynamic-yield/dynamic-yield.queries';
import { doesWindowExist } from '../../../utils/dom/dom.utils';
import { PlaceholderImage } from '../../images/placeholder-image/placeholder-image.component';
import { ProductCard } from '../../product-components/product-card/product-card.component';
import { dynamicYieldCards, dynamicYieldCardsContainer } from './dy-product-recommendations.css';

const SKELETON_CARD_COUNT = 6;

const ProductCardSkeleton: FunctionComponent = () => (
	<div className="pa2 w-100 lh-title dib measure-narrow" data-testid="product-card-skeleton">
		{/* Image placeholder */}
		<div className="w-100">
			<PlaceholderImage width={214} height={214} className="w-100" bypassLazyLoad />
		</div>
		{/* Content placeholder */}
		<div className="flex flex-column ph2 ph0-m f7 f6-ns">
			{/* Brand and title placeholder */}
			<div className="lh-copy mb1 min-h3-ns">
				<PlaceholderImage className="br2 mb1 w-80" height={14} />
				<PlaceholderImage className="br2 mb1 w-100" height={14} />
				<PlaceholderImage className="br2 w-60" height={14} />
			</div>
			{/* Rating placeholder */}
			<div className="mb1 h1 f6">
				<PlaceholderImage className="br2" width={80} height={14} />
			</div>
			{/* Price placeholder */}
			<div className="mb1 f7">
				<PlaceholderImage className="br2" width={60} height={16} />
			</div>
		</div>
	</div>
);

const ProductCardSkeletonList: FunctionComponent = () => (
	<>
		{Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
			<div
				key={`skeleton_${index}`}
				className={`dib db-ns v-top overflow-hidden ${dynamicYieldCards} ma0`}
				data-testid="dyRecommendationSkeleton">
				<ProductCardSkeleton />
			</div>
		))}
	</>
);

export type DyProductRecommendationsProps = {
	campaign: string;
	variantId: number;
	trackingId: string;
};

export const DyProductRecommendations: FunctionComponent<DyProductRecommendationsProps> = ({ campaign, variantId }) => {
	const { pathname } = useRouterLocation();
	const { data: recommendationsData, error } = useQuery<RecommendationsQuery, RecommendationsQueryVariables>(RECOMMENDATIONS, {
		variables: {
			id: variantId,
			campaign,
			location: pathname,
			usePlaLinks: true
		},
		skip: !doesWindowExist()
	});

	const scrollToTop = () => window.scrollTo(0, 0);

	const recommendationsList = recommendationsData?.recommendations;

	return (
		<section data-testid={`dy-${campaign}-${variantId}`} style={{ minHeight: '380.5px' }}>
			<div className={'ph0 ph3-ns pt3 pb2 f4 font-medium tl bb-ns b--theme-grey-light'}>Recommended for You</div>
			<div
				className={`f5 f6-m nowrap ws-normal-ns flex-ns flex-wrap-ns mt4-ns mh0 ph0 overflow-x-auto scroll-snap-x overflow-x-hidden-ns overflow-y-hidden ${dynamicYieldCardsContainer}`}>
				{!recommendationsData && !error ? (
					<ProductCardSkeletonList />
				) : recommendationsList && recommendationsList.length > 0 ? (
					recommendationsList.map((dyRec, index) => (
						// click handler is only being used for analytics
						// eslint-disable-next-line jsx-a11y/click-events-have-key-events
						<div
							role="button"
							key={`dyrec_${index}`}
							className={`dib db-ns v-top overflow-hidden ${dynamicYieldCards} ma0`}
							onClick={scrollToTop}
							data-testid="dyRecommendation"
							tabIndex={0}>
							<ProductCard
								image={{
									description: dyRec.image.description,
									publicID: dyRec.image.id,
									options: defaultCloudinaryOptions,
									useDimensions: false
								}}
								brand={dyRec.brandName}
								title={dyRec.title}
								url={dyRec.url}
								serverAnalytics={{
									mutation: REPORT_RECOMMENDATION_ENGAGEMENT,
									variables: {
										slotId: dyRec.id
									}
								}}
								analyticsEvent={{ data: buildGTMFindingMethodDynamicRecommendation() }}
								ratingValue={dyRec.ratingValue}
								priceInfo={{ current: dyRec.price }}
							/>
						</div>
					))
				) : (
					<div className="font-medium f5 mt4">No recommendations available at this time.</div>
				)}
			</div>
		</section>
	);
};
